import { useMemo, useCallback, useEffect, useRef } from "react";
import type { JSONContent } from "@tiptap/react";
import type { ContentNode } from "@/types/template";
import type { SubTemplateData } from "@/types/evaluation";
import { parseFieldDef } from "@/lib/parseFieldDef";
import { calcScoreGroup } from "@/utils/scoring";
import {
  documentToReportSections,
  buildReportLineMap,
} from "@/components/unified/documentToReport";

interface ReportSection {
  sectionId?: string;
  lines: string[];
}

type ScoreGroupProp = "sum" | "min" | "max" | "avg" | "count";

function isScoreGroupProp(prop: string | undefined): prop is ScoreGroupProp {
  return prop === "sum" || prop === "min" || prop === "max" || prop === "avg" || prop === "count";
}

function resolveScoreGroupProp(
  result: ReturnType<typeof calcScoreGroup>,
  prop: ScoreGroupProp,
): string {
  if (prop === "avg") {
    return String(Math.round(result.avg * 10) / 10);
  }
  return String(result[prop]);
}

function extractScoringContent(content: JSONContent[] | undefined): ContentNode[] {
  if (!content) {
    return [];
  }
  const nodes: ContentNode[] = [];
  for (const n of content) {
    if (n.type === "scoregroupNode") {
      nodes.push({ id: (n.attrs?.id as string | undefined) ?? "", type: "scoregroup" });
    } else if (n.type === "fieldNode") {
      const field = parseFieldDef((n.attrs?.fieldDef as string | undefined) ?? "");
      if (field) {
        nodes.push({ field, type: "field" });
      }
    }
  }
  return nodes;
}

function useInterpolate(
  scoringContent: ContentNode[],
  values: Record<string, unknown>,
  variableValues: Record<string, string>,
) {
  return useCallback(
    (text: string) => {
      if (typeof text !== "string") {
        return String(text);
      }
      return text.replaceAll(/\{([^{}]+)\}/g, (match, key: string) => {
        const [groupId, prop] = key.split(":");
        if (groupId && isScoreGroupProp(prop)) {
          const result = calcScoreGroup(scoringContent, groupId, values);
          return resolveScoreGroupProp(result, prop);
        }
        if (variableValues[key]) {
          return variableValues[key];
        }
        return match;
      });
    },
    [scoringContent, values, variableValues],
  );
}

export function useReportGeneration(
  unifiedDocument: JSONContent | null,
  values: Record<string, unknown>,
  subTemplateValues: Record<string, SubTemplateData>,
  variableValues: Record<string, string>,
  sectionToggles: Record<string, boolean>,
  fieldToggles: Record<string, boolean>,
) {
  const scoringContent = useMemo(
    () => extractScoringContent(unifiedDocument?.content),
    [unifiedDocument],
  );

  const interpolate = useInterpolate(scoringContent, values, variableValues);

  const reportSections = useMemo<ReportSection[]>(
    () =>
      documentToReportSections(
        unifiedDocument ?? { content: [], type: "doc" },
        values,
        subTemplateValues,
        interpolate,
        sectionToggles,
        fieldToggles,
      ),
    [unifiedDocument, values, subTemplateValues, interpolate, sectionToggles, fieldToggles],
  );

  const reportLineMapRef = useRef<number[]>([]);
  const reportLineMap = useMemo(
    () =>
      buildReportLineMap(
        unifiedDocument ?? { content: [], type: "doc" },
        values,
        subTemplateValues,
        interpolate,
        sectionToggles,
        fieldToggles,
      ),
    [unifiedDocument, values, subTemplateValues, interpolate, sectionToggles, fieldToggles],
  );
  useEffect(() => {
    reportLineMapRef.current = reportLineMap;
  }, [reportLineMap]);

  return { interpolate, reportLineMap, reportLineMapRef, reportSections, scoringContent };
}
